import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  ContentWrapper,
  HighlightsContainer,
  HandCraftedDesignsBlock,
  HighlightsBlock,
  HighlightSectionWrapper,
  DesignPlaceholderCloudImage,
  DesignImageBox,
  DesignImagesWrapper,
  DesignImageBoxFirstItem,
} from './AllTheLittleDetailsStatic.styles';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import ScrollMagic from 'scrollmagic';
import { gsap } from 'gsap';
import { useTheme } from '@styledComponents';
import { allTheLittleDetailsData } from '@data-ts/components/AllTheLittleDetails';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';

const renderHighlightsBlocks = () => {
  return allTheLittleDetailsData.blocks.map((block, index) => {
    if (index === 0) {
      return (
        <HandCraftedDesignsBlock
          svgIcon={block.icon}
          iconColor="accentBlue4"
          key={index}
          titleTagName="h3"
          titleVariant="featureHed17"
          titleColor="neutra6"
          bodyVariant="featureCopy17"
          bodyColor="neutra5"
          title={block.title}
          body={block.body}
        />
      );
    }
    return (
      <HighlightsBlock
        svgIcon={block.icon}
        iconColor="accentBlue4"
        key={index}
        titleTagName="h3"
        titleVariant="featureHed17"
        titleColor="neutra6"
        bodyVariant="featureCopy17"
        bodyColor="neutra5"
        title={block.title}
        body={block.body}
      />
    );
  });
};

export const AllTheLittleDetailsStatic: React.FC = () => {
  const sectionId = 'All_Little_Details_Static';
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const theme = useTheme();

  useLayoutEffect(() => {
    let allTheLittleDetailsStaticScene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let allTheLittleDetailsStaticTimeline: ReturnType<typeof gsap.timeline>;
    const contentWrapperEl = contentWrapperRef.current;
    const highlightTextElements = contentWrapperEl?.querySelectorAll('.customize-theme-highlights-block');
    const designImagesElements = contentWrapperEl?.querySelectorAll('.customized-theme-image-block');
    if (scrollController && isMounted && contentWrapperEl && highlightTextElements && designImagesElements) {
      const translateY = '-150vh';
      const translateLastTextY = '-140vh';
      const textDuration = 0.25;
      const textPosition = '-=0.1';
      const initCustomizeThemeTimeline = () => {
        allTheLittleDetailsStaticTimeline = gsap.timeline({ paused: true });
        allTheLittleDetailsStaticTimeline.to(highlightTextElements[0], {
          y: '-=120vh',
          duration: textDuration,
        });

        allTheLittleDetailsStaticTimeline.to(
          highlightTextElements[1],
          {
            y: translateY,
            duration: textDuration,
            onStart: function() {
              designImagesElements[0].classList.add('design-image-hide');
              designImagesElements[1].classList.add('design-image-visible');
            },
            onReverseComplete: function() {
              designImagesElements[1].classList.remove('design-image-visible');
              designImagesElements[0].classList.remove('design-image-hide');
            },
          },
          textPosition
        );
        allTheLittleDetailsStaticTimeline.to(
          highlightTextElements[2],
          {
            y: translateY,
            duration: textDuration,
            onStart: function() {
              designImagesElements[1].classList.remove('design-image-visible');
              designImagesElements[2].classList.add('design-image-visible');
            },
            onReverseComplete: function() {
              designImagesElements[2].classList.remove('design-image-visible');
              designImagesElements[1].classList.add('design-image-visible');
            },
          },
          textPosition
        );
        allTheLittleDetailsStaticTimeline.to(
          highlightTextElements[3],
          {
            y: translateLastTextY,
            duration: textDuration,
            onStart: function() {
              designImagesElements[2].classList.remove('design-image-visible');
              designImagesElements[3].classList.add('design-image-visible');
            },
            onReverseComplete: function() {
              designImagesElements[3].classList.remove('design-image-visible');
              designImagesElements[2].classList.add('design-image-visible');
            },
          },
          textPosition
        );
        allTheLittleDetailsStaticTimeline.to(
          highlightTextElements[4],
          {
            y: translateLastTextY,
            duration: textDuration,
            onStart: function() {
              designImagesElements[3].classList.remove('design-image-visible');
              designImagesElements[4].classList.add('design-image-visible');
            },
            onReverseComplete: function() {
              designImagesElements[4].classList.remove('design-image-visible');
              designImagesElements[3].classList.add('design-image-visible');
            },
          },
          textPosition
        );
        allTheLittleDetailsStaticTimeline.to(
          highlightTextElements[5],
          {
            y: translateLastTextY,
            duration: textDuration,
            onStart: function() {
              designImagesElements[4].classList.remove('design-image-visible');
              designImagesElements[5].classList.add('design-image-visible');
            },
            onReverseComplete: function() {
              designImagesElements[5].classList.remove('design-image-visible');
              designImagesElements[4].classList.add('design-image-visible');
            },
          },
          textPosition
        );
        allTheLittleDetailsStaticTimeline.to(contentWrapperRef.current, { duration: 0.1 }, 0);
      };

      const initCustomizeThemeScene = () => {
        allTheLittleDetailsStaticScene = new ScrollMagic.Scene({
          triggerElement: `#${sectionId}`,
          triggerHook: 0,
          duration: '500%',
        }).addTo(scrollController);
        allTheLittleDetailsStaticScene.setPin(`#${sectionId}`);
        allTheLittleDetailsStaticScene.on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          allTheLittleDetailsStaticTimeline?.progress(event.progress * 0.8);
        });
      };
      initCustomizeThemeTimeline();
      initCustomizeThemeScene();
    }
    const clearScene = () => {
      if (allTheLittleDetailsStaticTimeline) {
        allTheLittleDetailsStaticTimeline.progress(0);
        allTheLittleDetailsStaticTimeline.kill();
      }

      if (allTheLittleDetailsStaticScene) {
        allTheLittleDetailsStaticScene.destroy(true);
      }
    };

    return () => {
      clearScene();
    };
  }, [isMounted, scrollController, contentWrapperRef, theme]);

  const [isVisible, setIsVisible] = useState(false);
  return (
    <ContentWrapper id={sectionId} ref={contentWrapperRef} backgroundColor="humana1Light" paddingTop={[8, 0]} paddingBottom={[8, 10]}>
      <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
        <div />
      </LazyLoadWrapper>
      <HighlightsContainer>
        <HighlightSectionWrapper>{renderHighlightsBlocks()}</HighlightSectionWrapper>
      </HighlightsContainer>
      <DesignImagesWrapper paddingTop={[8, 12, 14]} paddingBottom={[0, 10, 12]}>
        {allTheLittleDetailsData.blocks.map((item, index) => {
          if (index === 0) {
            return (
              <DesignImageBoxFirstItem key={index}>
                <DesignPlaceholderCloudImage isMainSrcShown={isVisible} src={item.image} optimization="default" />
              </DesignImageBoxFirstItem>
            );
          } else {
            return (
              <DesignImageBox key={index}>
                <DesignPlaceholderCloudImage isMainSrcShown={isVisible} src={item.image} optimization="default" />
              </DesignImageBox>
            );
          }
        })}
      </DesignImagesWrapper>
    </ContentWrapper>
  );
};
